import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { MdEmail } from 'react-icons/md';
import ModalLayout from '../layouts/ModalLayout';
import Button from '../buttons/Button';
import { Connection } from '../../../redux/slices/connectionsSlice';
import localContactService from '../../../services/community/localContactService';
import { isValidEmail } from '../../../utils/stringUtils';
import { ProfileDataType } from '../../../types/Profile';
import { createEmptyField } from '../../../utils/formatUtils';

interface AddEmailModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  connection: Connection;
  onEmailAdded?: () => void;
}

export default function AddEmailModal({
  open,
  setOpen,
  connection,
  onEmailAdded,
}: AddEmailModalProps): JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const validateEmail = (emailValue: string): boolean => {
    if (!emailValue) {
      setError(t('invalid.empty'));
      return false;
    }
    if (!isValidEmail(emailValue)) {
      setError(t('invalid.invalid'));
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateEmail(email)) return;

    setIsSubmitting(true);
    try {
      const currentContact = await localContactService.fetchLocalContact(connection.externalMemberId ?? 0);
      const updatedReceivedData = [...currentContact.receivedData];
      const existingEmailIndex = updatedReceivedData.findIndex((field) => field.dataType === ProfileDataType.EMAIL);
      
      if (existingEmailIndex >= 0) {
        updatedReceivedData[existingEmailIndex] = {
          ...updatedReceivedData[existingEmailIndex],
          email,
        };
      } else {
        const newEmailField = createEmptyField(ProfileDataType.EMAIL);
        newEmailField.email = email;
        updatedReceivedData.push(newEmailField);
      }

      await localContactService.updateLocalContact(
        connection.externalMemberId!,
        currentContact.firstName,
        currentContact.lastName,
        currentContact.note,
        updatedReceivedData,
        currentContact.customFieldResponses,
      );

      await localContactService.inviteContacts([connection.externalMemberId!]);
      
      toast.success(t('toast.success.inviteSent_one'));
      setOpen(false);
      setEmail('');
      setError('');
      
      if (onEmailAdded) onEmailAdded();
    } catch (err) {
      console.error(err);
    }

    setIsSubmitting(false);
  };

  const handleClose = (): void => {
    setOpen(false);
    setEmail('');
    setError('');
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setEmail(value);
    if (error) {
      validateEmail(value);
    }
  };

  return (
    <ModalLayout
      open={open}
      setOpen={handleClose}
      className="z-50 w-full max-w-md rounded-[12px] bg-secondary-50 p-6">
      <div className="flex flex-col">
        <div className="mb-4 flex items-center">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-secondary-50">
            <MdEmail className="h-6 w-6" />
          </div>
          <div className="ml-4">
            <h2 className="text-xl font-semibold text-primary-900">
              {t('component.modal.addEmail.title')}
            </h2>
            <p className="text-sm text-gray-600">
              {t('component.modal.addEmail.subtitle', {
                name: `${connection.fields.FIRST_NAME} ${connection.fields.LAST_NAME}`,
              })}
            </p>
          </div>
        </div>

        <div className="mb-6">
          <p className="mb-4 text-sm text-gray-700">
            {t('component.modal.addEmail.description')}
          </p>
          
          <div className="relative">
            <label htmlFor="email-input" className="mb-2 block text-sm font-medium text-gray-700">
              {t('dataType.email')} *
            </label>
            <input
              id="email-input"
              type="email"
              value={email}
              onChange={handleEmailChange}
              onBlur={() => validateEmail(email)}
              placeholder={t('dataType.email')}
              className={classNames(
                'w-full rounded-lg border px-3 py-2 text-gray-600 focus:border-primary-300 focus:outline-none focus:ring-1 focus:ring-primary-300',
                {
                  '!border-danger': error,
                }
              )}
              disabled={isSubmitting}
            />
            {error && (
              <p className="mt-1 text-sm text-danger">{error}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="tertiary"
            onClick={handleClose}
            disabled={isSubmitting}>
            {t('general.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !email}>
            {isSubmitting ? t('general.loading') : t('component.modal.addEmail.sendInvite')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}
